import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GridOptions, ColGroupDef, ColDef, GridApi, createGrid } from 'ag-grid-community';
import { DecimalPipe } from '@angular/common';
import { first } from 'rxjs/operators';
import { GridCheckboxComponent } from '../../../components/grid/grid-checkbox/grid-checkbox.component';
import { CustomHeaderIconComponent } from '../../../components/grid/custom-header-icon/custom-header-icon.component';
import { AdminService } from '../../../services/admin.service';
import { CompaniesService } from '../../../services/companies.service';
import { PayrollService } from '../../../services/payroll.service';
import { AuthService } from '../../../services/auth.service';
import { ModalService } from '../../../services/modal.service';
import { AuditService } from '../../../services/audit.service';
import { ModalStates } from '../../../models/modal';
import {
  Earning,
  EmployeePay,
  MasterPayrollObject,
  Payline,
  PayrollConfigSetupData
} from '../../../models/payroll';
import {
  CompanyEarningConfig,
  EarningCalculations,
  SpecialUseEarning
} from '../../../models/company-earning-config';
import {
  CompanyDeductionConfig,
  DeductionCalculations
} from '../../../models/company-deduction-config';
import { CellEditorComponent } from '../../../components/grid/cell-editor/cell-editor.component';
import { CellRendererComponent } from '../../../components/grid/cell-renderer/cell-renderer.component';
import { PayrollManagement } from '../../../tools/payroll-management';
import { AutoPayrollSkipModal } from '../auto-payroll-skip-modal/auto-payroll-skip-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'auto-payroll-grid',
  templateUrl: './auto-payroll-grid.component.html',
  styleUrls: ['./auto-payroll-grid.component.css'],
  standalone: false
})
export class AutoPayrollGridComponent implements OnInit {
  clientCode: string;
  columnCount = 0;
  columnDefs = [];
  columnTypes = {
    immutable: {
      suppressNavigable: true,
      cellClass: 'immutable-cell'
    }
  };
  configSetupData: PayrollConfigSetupData;
  defaultCellRendererParams = { auditContext: 'Automatic Payroll' };
  deductionConfigs: Record<string, CompanyDeductionConfig> = {};
  earningConfigs: Record<string, CompanyEarningConfig> = {};
  frameworkComponents;
  gridApi: GridApi<any>;
  gridHeight = '700px';
  gridOptions: GridOptions = {
    animateRows: true,
    enableCellChangeFlash: true,
    // callback sets a grid row id based on a data value
    // here RowId is populated with PaylineId
    // grid row Ids should always be strings
    getRowId: params => {
      return params.data.RowId;
    },
    onGridReady: params => {
      // grid is "ready",  but data is not
      this.gridApi = params.api;
    },
    onCellValueChanged: params => {
      // FIXME: handle future scenario where 1 person has multiple paylines
      // when OkToPay is toggled, all the paylines for that person should change
      if (params.oldValue || params.newValue) {
        // ensure number directive changes do not trigger a change event.
        // e.g. 15.5 to 15.50 is not a change
        if (
          !isNaN(parseFloat(params.oldValue)) &&
          !isNaN(parseFloat(params.newValue)) &&
          parseFloat(params.oldValue) === parseFloat(params.newValue)
        ) {
          return;
        }

        // Bug 8251: when clearing an input,
        // set the value to 0 rather than empty string
        // so the Base can determine if the TemporaryRate/Hours
        // were never set or explicity cleared by the user
        if (!params.newValue && params.oldValue) {
          params.data[params.colDef.field] = 0;
        }

        // update payline.changedColumns
        const rowNodes = [params.node];
        const line = this.paylines[params.data.RowId];
        if (!line.ChangedColumns) {
          line.ChangedColumns = [params.colDef.field];
          this.gridApi.redrawRows({ rowNodes });
        } else if (!line.ChangedColumns.includes(params.colDef.field)) {
          line.ChangedColumns.push(params.colDef.field);
          this.gridApi.redrawRows({ rowNodes });
        }

        this.isLoading = true;
        const changedData = [params.data];
        params.api.applyTransaction({ update: changedData });
        this._save();

        const data = {
          ClientCode: this.clientCode,
          PersonId: params.data.PersonId,
          PayrollId: this.payGridId,
          PeriodEnd: this.payrollMgmt.periodEndDisplay(this.payroll),
          PayDate: this.payroll.PayDate,
          LocationId: this.payroll.LocationId
        };

        data[params.colDef.field] =
          `{ "oldVal": "${params.oldValue}", "newVal": "${params.newValue}" }`;

        this.auditService.audit(`auto-payroll-row-updated`, data);
      }
    },
    // NOTE: this is turned on in order to auto size all columns
    // on grid ready, not just the ones visible.
    // if column virtualization is needed, a solution for that will need
    // to be found
    suppressColumnVirtualisation: true
  };
  isLoading: boolean;
  payGridId: number;
  paylines: Record<number, Payline>; // quick lookup of paylines by id
  payroll: MasterPayrollObject;
  render = false;
  rowData = [];

  constructor(
    private adminService: AdminService,
    private auditService: AuditService,
    private authService: AuthService,
    private companyService: CompaniesService,
    private decimalPipe: DecimalPipe,
    private payrollService: PayrollService,
    public payrollMgmt: PayrollManagement,
    private modalService: ModalService,
    private ngModalService: NgbModal,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.clientCode = this.route.snapshot.paramMap.get('client_code');
    this.payGridId = +this.route.snapshot.paramMap.get('id');

    // check to make sure this is a valid payroll request
    this.companyService.getLocations(this.clientCode).subscribe(locationConfigs => {
      // if client is NOT setup to auto pay - bounce them to "normal" payroll
      if (this.payrollMgmt.isClientSetupForAutoPayroll(locationConfigs)) {
        this._initializeGrid();
      } else {
        this.router.navigate([`/client/${this.clientCode}/payroll`]);
      }
    });
  }

  ngOnInit() {}

  onCellKeyDown(params) {
    // if OkToPay column is selected and space bar is pressed, toggle value
    if (params.colDef.field === 'OkToPay' && params.event.code === 'Space') {
      // assignment of data.RowId to grid row's data Id is set in the parent grid component
      const rowNode = params.api.getRowNode(params.data.RowId);
      rowNode.setDataValue('OkToPay', !params.data.OkToPay);
    }
  }

  regenerateWorksheet() {
    // show modal
    this.modalService.showConfirmModal(
      `All changes made to this worksheet will be lost when the worksheet is regenerated. Are you sure you want to regenerate the worksheet?`,
      `No, keep my changes `,
      'Regenerate Worksheet'
    );
    this.modalService.confirmModalCallback.pipe(first()).subscribe((confirm: boolean) => {
      if (confirm) {
        this.isLoading = true;
        // delete current grid
        this.payrollService.deletePayGrid(this.clientCode, this.payGridId).subscribe(() => {
          // initialize new grid
          this.payrollService
            .initializePayGrid(
              this.clientCode,
              this.payroll.Calendars,
              this.payroll.LocationId,
              this.payroll.PayDate,
              this.payroll.PayType,
              null,
              this.authService.getCurrentUserName()
            )
            .then(payroll => {
              // save new grid to database
              this.payrollService.createPayGrid(this.clientCode, payroll).subscribe(payGridId => {
                // navigate to new grid
                this.router
                  .navigate([`/client/${this.clientCode}/autopayroll/${payGridId}`])
                  .then(() => {
                    window.location.reload();
                  });
              });
            });
        });

        this.auditService.audit(`auto-payroll-regenerated`, {
          ClientCode: this.clientCode,
          PayrollId: this.payGridId,
          PeriodEnd: this.payrollMgmt.periodEndDisplay(this.payroll),
          PayDate: this.payroll.PayDate,
          LocationId: this.payroll.LocationId
        });
      }
    });
  }

  skipAutomaticPay() {
    var modalRef = this.ngModalService.open(AutoPayrollSkipModal, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'centered'
    });

    const c = modalRef.componentInstance as AutoPayrollSkipModal;

    c.payDate = this.payroll.PayDate;
    c.periodEndDate = this.payroll.Calendars[0].PeriodEnd;

    c.onSkip = () => {
      this.confirmSkipAutoPay().subscribe(() => {
        modalRef.close();
        this.auditPayrollSkipped();
        this.router.navigate([`/client/${this.clientCode}/payroll-landing`]);
      });
    };

    c.onSkipAndNavigate = () => {
      this.confirmSkipAutoPay().subscribe(() => {
        modalRef.close();
        this.auditPayrollSkipped();
        this.router.navigate([`/client/${this.clientCode}/payroll`]);
      });
    };

    c.onCancel = () => {
      modalRef.close();
    };
  }

  private confirmSkipAutoPay() {
    this.isLoading = true;
    this.payroll.LastEditOn = new Date();
    this.payroll.LastEditBy = this.authService.getCurrentUserName();
    this.payroll.IsAutomaticSkipped = true;
    return this.payrollService.updatePayGrid(this.clientCode, this.payGridId, this.payroll);
  }

  private auditPayrollSkipped() {
    this.auditService.auditPayroll(`auto-payroll-skipped`, {
      ClientCode: this.clientCode,
      PayrollId: this.payroll.Id,
      PayDate: this.payroll.PayDate,
      LocationId: this.payroll.LocationId,
      Calendars: this.payroll.Calendars,
      PayrollType: this.payroll.PayType
    });
  }

  // #region PRIVATE METHODS

  private _autoSizeAllColumns() {
    // auto size all columns to fit the width of the content
    // if column virtualization is on, only columns in DOM be auto sized
    const allColumnIds = [];
    this.gridApi.getColumns().forEach(column => {
      allColumnIds.push(column.getId());
    });

    this.gridApi.autoSizeColumns(allColumnIds);
  }

  private _autoSizeGridHeight() {
    // NOTE: this is a workaround to set the grid height to fit the number of rows
    // grid header rows (with top group row) ~100px
    // each data row is ~45px
    // 20px for horizonal scroll bar
    // max height currently set to 700px
    this.gridHeight = 100 + this.rowData.length * 45 + 20 + 'px';
  }

  private _buildDeductionColumns() {
    const deductionGroup: ColGroupDef = {
      headerName: 'Deductions',
      children: []
    };

    const deductions = this.configSetupData.Deductions.sort(
      (a: CompanyDeductionConfig, b: CompanyDeductionConfig) => {
        return a.AppearanceSequence - b.AppearanceSequence;
      }
    );

    deductions.forEach(deduction => {
      // task 7559 - only display deductions where config.DisplayInGrid == true
      if (!deduction.DisplayInGrid) {
        return;
      }

      // skip deduction if this.payroll.Config.HoldDeductions
      if (this.payroll.Config.HoldDeductions.includes(deduction.Id)) {
        return;
      }

      deductionGroup.children.push({
        field: deduction.Name,
        type: ['immutable'],
        columnGroupShow: 'closed',
        minWidth: 115,
        resizable: true,
        valueFormatter: params => {
          return params.value ? '$' + this.decimalPipe.transform(params.value, '1.2-2') : '';
        }
      });

      this.columnCount++;
    });

    // show first column when group is collapsed
    if (deductionGroup.children.length) {
      deductionGroup.children[0].columnGroupShow = null;
    }

    return deductionGroup;
  }

  private _buildEarningColumnDefinition(earning: CompanyEarningConfig) {
    const columnsDefs: ColDef[] = [];

    let columnDef: ColDef = {
      headerName: earning.Name,
      editable: params => {
        // if there is a key available in this employee's payline
        // allow editing
        return Object.keys(params.data).includes(params.colDef.field);
      },
      cellEditor: CellEditorComponent,
      cellRenderer: CellRendererComponent,
      columnGroupShow: 'closed',
      minWidth: 115,
      cellStyle: params => this._setCellStyle(params, { padding: '0' }),
      headerComponent: CustomHeaderIconComponent,
      resizable: true
    };

    // apply various formatting and assign font-awesome icon
    if (
      earning.CalculationMethod === EarningCalculations['Fixed Dollar'] &&
      earning.SpecialUseEarning === SpecialUseEarning.Other
    ) {
      columnDef.headerComponentParams = { icon: 'fas fa-dollar-sign' };
      columnDef.field = `${earning.Name}Rate`;

      let cellParams = { mode: 'currency', decimals: 2 };
      columnDef.cellEditorParams = cellParams;
      columnDef.cellRendererParams = cellParams;

      columnsDefs.push({ ...columnDef });
      this.columnCount++;
    }

    if (earning.CanSetHours) {
      columnDef.headerComponentParams = { icon: 'far fa-clock' };
      columnDef.field = `${earning.Name}Hours`;

      let cellParams = { mode: 'number', decimals: 2 };
      columnDef.cellEditorParams = cellParams;
      columnDef.cellRendererParams = cellParams;

      columnsDefs.push({ ...columnDef });
      this.columnCount++;
    }

    return columnsDefs;
  }

  private _buildEarningColumns() {
    const earningsGroup: ColGroupDef = {
      headerName: 'Earnings',
      children: []
    };

    earningsGroup.children = this.configSetupData.Earnings
      // task 7882 - only display earnings where config.DisplayInGrid == true
      // skip earning if this.payroll.Config.HoldEarnings
      .filter(
        earning => earning.DisplayInGrid || !this.payroll.Config.HoldEarnings.includes(earning.Id)
      )
      .sort((a: CompanyEarningConfig, b: CompanyEarningConfig) => {
        return a.AppearanceSequence - b.AppearanceSequence;
      })
      // build out earning column definitions
      .flatMap(earn => {
        return this._buildEarningColumnDefinition(earn);
      });

    // show first column when group is collapsed
    if (earningsGroup.children.length) {
      earningsGroup.children[0].columnGroupShow = null;
    }

    return earningsGroup;
  }

  private _buildGridDeductionData(row: any, line: Payline, pay: EmployeePay) {
    line.Deductions.forEach(deduction => {
      const config = this.deductionConfigs[deduction.DeductionCode];
      // deduction fields are currently read only. formatted value and prefix w/ dollar sign
      if (config.CalculationMethod === DeductionCalculations.Fixed) {
        row[config.Name] = deduction.PermanentAmount || '';
      } else if (config.CalculationMethod === DeductionCalculations.Rate) {
        row[config.Name] = deduction.PermanentRate || '';
      }
    });
  }

  private _buildGridDefinitions() {
    this.configSetupData.Deductions.forEach(config => {
      this.deductionConfigs[config.Id] = config;
    });

    this.configSetupData.Earnings.forEach(config => {
      this.earningConfigs[config.Id] = config;
    });

    this.columnCount = 4;

    this.columnDefs = [
      {
        field: 'OkToPay',
        headerName: 'OK to Pay',
        cellRenderer: GridCheckboxComponent,
        pinned: 'left',
        cellStyle: params => this._setCellStyle(params)
      },
      { field: 'Id', type: ['immutable'], pinned: 'left', resizable: true },
      {
        field: 'Name',
        type: ['immutable'],
        sortable: true,
        sortingOrder: ['asc', 'desc'],
        pinned: 'left',
        flex: 1,
        resizable: true
      },
      { field: 'Rate', type: ['immutable'], pinned: 'left', resizable: true },
      this._buildEarningColumns(),
      this._buildDeductionColumns()
    ];
  }

  private _buildGridEarningData(row: any, line: Payline, pay: EmployeePay) {
    // rules for the 'Rate' column
    // if an employee has both a non zero Salary and Reg earning rate - display 'both'
    // else display either one if available. for REG, append '/hr'
    let salary: Earning = null;
    let reg: Earning = null;

    line.Earnings.forEach(earning => {
      // earning columns are named as such
      // {earningName}Hours, // {earningName}Rate

      const config = this.earningConfigs[earning.EarningId];

      if (config.CanSetHours) {
        row[`${config.Name}Hours`] = earning.Hours;
      }

      if (
        config.CalculationMethod === EarningCalculations['Fixed Dollar'] &&
        config.SpecialUseEarning == SpecialUseEarning.Other
      ) {
        row[`${config.Name}Rate`] =
          earning.TemporaryRateAmount || parseInt(`${earning.TemporaryRateAmount}`) == 0
            ? earning.TemporaryRateAmount
            : earning.PermanentRateAmount || '';
      }

      if (config.SpecialUseEarning == SpecialUseEarning.Salary && earning.PermanentRateAmount > 0) {
        salary = earning;
      } else if (
        config.SpecialUseEarning == SpecialUseEarning.Regular &&
        earning.PermanentRateAmount > 0
      ) {
        reg = earning;
      }
    });

    if (salary !== null && reg != null) {
      row.Rate = 'both';
    } else if (salary) {
      row.Rate = '$' + this.decimalPipe.transform(salary.PermanentRateAmount, '1.2-2');
    } else if (reg) {
      row.Rate = '$' + this.decimalPipe.transform(reg.PermanentRateAmount, '1.2-4') + '/hr';
    }
  }

  private _buildGridRowData() {
    // populate grid with payroll data
    const rows = [];
    this.payroll.EmployeePays.forEach(pay => {
      pay.Paylines.forEach(line => {
        const row = {
          RowId: line.PaylineId,
          OkToPay: line.OkToPay,
          Id: pay.EmployeeNumber,
          Name: this.payrollMgmt.getEmployeeFullName(pay),
          Rate: null,
          PersonId: pay.PersonId
        };

        this._buildGridEarningData(row, line, pay);
        this._buildGridDeductionData(row, line, pay);

        rows.push(row);
      });
    });

    this.rowData = rows;
  }

  private _initializeGrid() {
    this.payrollService.getPayGrid(this.clientCode, this.payGridId).subscribe(payroll => {
      this.payroll = payroll;

      // build dict for quick lookup of payline
      this.paylines = {};

      // bug 6956
      if (!this.payroll.EmployeePays || !this.payroll.EmployeePays.length) {
        this.modalService.showAlertModal(
          'Critical Error. There are no people set up for this client. Please exit this payroll worksheet and add people to continue.',
          ModalStates.Error
        );
        this.render = true;
        return;
      }

      this.payroll.EmployeePays.forEach(pay => {
        pay.Paylines.forEach(line => {
          this.paylines[line.PaylineId] = line;
        });
      });

      this.render = true;

      this.payrollService
        .getPayrollConfigSetupData(
          this.clientCode,
          this.payroll.Calendars,
          this.payroll.LocationId,
          this.payroll.PayDate,
          this.payroll.PayType
        )
        .subscribe(config => {
          this.configSetupData = config;
          this.payrollMgmt.sortEmployeePays(this.payroll.EmployeePays, this.configSetupData);
          this._buildGridDefinitions();
          this._buildGridRowData();
          this._gridDataReady();
        });
    });
  }

  private _gridDataReady() {
    // NOTE: if gridoptions.api/columnApi are undefined,
    // check ngIf logic on grid to make sure it has rendered
    this.isLoading = false;

    this._autoSizeGridHeight();

    setTimeout(() => {
      this._autoSizeAllColumns();
    });
  }

  private _save() {
    this._saveGridChanges();
    this.payroll.LastEditOn = new Date();
    this.payroll.LastEditBy = this.authService.getCurrentUserName();
    this.payrollService
      .updatePayGrid(this.clientCode, this.payGridId, this.payroll)
      .subscribe(() => {
        this.isLoading = false;
      });
  }

  private _saveEarnings(rowData, pay: EmployeePay, payline: Payline) {
    Object.keys(this.earningConfigs).forEach(id => {
      const config = this.earningConfigs[id];
      let earning = payline.Earnings.find(x => x.EarningId == id);

      // only earnings that came in on the original payline
      // are available to be edited
      if (earning) {
        earning.Hours = rowData[`${config.Name}Hours`];
        earning.TemporaryRateAmount = rowData[`${config.Name}Rate`];
      }
    });
  }

  // save from grid to master payroll object
  private _saveGridChanges() {
    // rowId == paylineId
    this.rowData.forEach(data => {
      this.payroll.EmployeePays.forEach(pay => {
        if (pay.PersonId == data.PersonId) {
          const line = this.paylines[data.RowId];
          line.OkToPay = data.OkToPay;
          this._saveEarnings(data, pay, line);
        }
      });
    });
  }

  private _setCellStyle(params, defaultStyle = {}) {
    const line = this.paylines[params.data.RowId];
    if (line.ChangedColumns && line.ChangedColumns.includes(params.colDef.field)) {
      return { ...defaultStyle, border: '1px solid #8fc067' };
    } else {
      return { ...defaultStyle };
    }
  }

  // #endregion
}
